import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getLogModes,
  getLogSourcesForMode,
  getHistoricalLogsByDate,
  getConsolidatedAlerts
} from '../Api';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { formatInTimeZone } from 'date-fns-tz';
import { startOfDay, endOfDay, formatISO, parseISO, format } from 'date-fns';
import CustomAudioPlayer from '../components/CustomAudioPlayer';

import './HistoricalLogs.css';

const sortOptions = [
    { value: 'asc', label: 'Timestamp Ascending (Oldest First)' },
    { value: 'desc', label: 'Timestamp Descending (Newest First)' },
];

const MODE_SPECIFIC_CONFIG_CLIENT = {
  'Bounce': {
    tagColumn: 'customer_sentiment',
    alertTags: ['Bounce_Occurred_Alert', 'High_Bounce_Risk']
  },
  'Audio': {
    // Multi-analysis mode support with field mappings
    fieldMappings: {
      'Customer_sentiment_analyzer': {
        primaryField: 'customer_sentiment',
        secondaryField: 'concern',
        tertiaryField: 'solution_provided',
        alertTags: ['Negative', 'Alert'],
        primaryLabel: 'Customer Sentiment',
        secondaryLabel: 'Concern',
        tertiaryLabel: 'Solution Provided'
      },
      'Referral_tracker': {
        primaryField: 'classification',
        secondaryField: 'evidence', 
        tertiaryField: 'confidence',
        alertTags: ['Referral'],
        primaryLabel: 'Classification',
        secondaryLabel: 'Evidence',
        tertiaryLabel: 'Confidence'
      },
      'Diversion_tracker': {
        primaryField: 'classification',
        secondaryField: 'evidence',
        tertiaryField: 'confidence', 
        alertTags: ['Diversion'],
        primaryLabel: 'Classification',
        secondaryLabel: 'Evidence',
        tertiaryLabel: 'Confidence'
      }
    },
    // Backward compatibility - default to customer sentiment
    tagColumn: 'customer_sentiment',
    alertTags: ['Negative', 'Alert']
  },
  'Idle': {
    idleDurationColumn: 'idle_duration_seconds',
    idleDurationThreshold: 300,
  },
  'Waiting_time': {
    waitTimeColumn: 'waiting_time_seconds',
    waitTimeThreshold: 300,
    alertTags: ['Long_Wait', 'Crowd_Warning']
  },
};

// Utility function to detect analysis mode from log entry
const detectAnalysisMode = (logEntry) => {
  if (!logEntry) return 'Customer_sentiment_analyzer';
  
  // Check for explicit analysis_mode field first
  if (logEntry.analysis_mode) {
    return logEntry.analysis_mode;
  }
  
  // Check for classification field (Referral/Diversion modes)
  if (logEntry.classification !== undefined) {
    // Distinguish between referral and diversion based on classification values
    const classification = String(logEntry.classification).toLowerCase();
    if (classification === 'referral' || classification === 'self') {
      return 'Referral_tracker';
    } else if (classification === 'diversion') {
      return 'Diversion_tracker';
    } else if (classification === 'neutral') {
      // Neutral can be used by both trackers - use explicit analysis_mode if available
      return logEntry.analysis_mode || 'Referral_tracker';
    }
    // Fallback for any other classification values
    return 'Referral_tracker';
  }
  
  // Check for customer_sentiment field (Customer sentiment mode)
  if (logEntry.customer_sentiment !== undefined) {
    return 'Customer_sentiment_analyzer';
  }
  
  // Default fallback
  return 'Customer_sentiment_analyzer';
};

// Get field mapping based on analysis mode
const getFieldMapping = (logEntry, explicitMode = null) => {
  const mode = explicitMode || detectAnalysisMode(logEntry);
  const audioConfig = MODE_SPECIFIC_CONFIG_CLIENT['Audio'];
  
  if (audioConfig.fieldMappings && audioConfig.fieldMappings[mode]) {
    return audioConfig.fieldMappings[mode];
  }
  
  // Fallback to customer sentiment analyzer
  return audioConfig.fieldMappings['Customer_sentiment_analyzer'];
};

// Get primary field value from log entry based on analysis mode  
const getPrimaryFieldValue = (logEntry) => {
  const fieldMapping = getFieldMapping(logEntry);
  return logEntry[fieldMapping.primaryField];
};

// Get all available tag values from logs based on analysis mode
const getAvailableTagsFromLogs = (logs, selectedMode) => {
  if (!logs.length || !selectedMode || selectedMode.label !== 'Audio') {
    return [];
  }
  
  const uniqueTags = new Set();
  logs.forEach(log => {
    const primaryValue = getPrimaryFieldValue(log);
    if (primaryValue) {
      uniqueTags.add(String(primaryValue));
    }
  });
  
  return Array.from(uniqueTags).sort();
};

// Get all available analysis modes from logs (for Audio mode only)
const getAvailableAnalysisModesFromLogs = (logs, selectedMode) => {
  if (!logs.length || !selectedMode || selectedMode.label !== 'Audio') {
    return [];
  }

  const uniqueAnalysisModes = new Set();
  logs.forEach(log => {
    const analysisMode = detectAnalysisMode(log);
    if (analysisMode) {
      uniqueAnalysisModes.add(analysisMode);
    }
  });

  // Only return if we have multiple analysis modes, otherwise no need to show filter
  const modes = Array.from(uniqueAnalysisModes).sort();
  return modes.length > 1 ? modes : [];
};

// Get dynamic column header label based on field key and log context  
const getColumnHeaderLabel = (key, logs, selectedMode) => {
  // For Audio mode, check if we need dynamic labeling
  if (selectedMode && selectedMode.label === 'Audio' && logs.length > 0) {
    const sampleLog = logs[0];
    const fieldMapping = getFieldMapping(sampleLog);
    
    // Return dynamic label if this key matches an analysis field
    if (key === fieldMapping.primaryField) {
      return fieldMapping.primaryLabel;
    } else if (key === fieldMapping.secondaryField) {
      return fieldMapping.secondaryLabel;
    } else if (key === fieldMapping.tertiaryField) {
      return fieldMapping.tertiaryLabel;
    }
  }
  
  // Default formatting for standard fields
  return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Get dynamic column order based on logs and analysis mode
const getDynamicColumnOrder = (logs, selectedMode) => {
  const baseColumns = ['event_timestamp_utc', 'level', 'message'];
  const endColumns = ['transcript', 'bounce_type', 'event_type', 'event', 'idle_duration_seconds', 
    'waiting_time_seconds', 'waiting_time_formatted', 'zone_id', 'start_time_utc', 'car_id', 'source_id', 
    'running_total', 'running_enter_count', 'running_exit_count', 'track_id'];
  
  // For Audio mode, determine analysis-specific columns
  if (selectedMode && selectedMode.label === 'Audio' && logs.length > 0) {
    const sampleLog = logs[0];
    const fieldMapping = getFieldMapping(sampleLog);
    
    const analysisColumns = [
      fieldMapping.primaryField,
      fieldMapping.secondaryField, 
      fieldMapping.tertiaryField
    ].filter(field => field); // Remove any undefined fields
    
    return [...baseColumns, ...analysisColumns, ...endColumns];
  }
  
  // Default/fallback column order (backward compatibility)
  return [
    'event_timestamp_utc', 'level', 'message', 'customer_sentiment',
    'transcript', 'concern', 'solution_provided', 'bounce_type',
    'event_type', 'event', 'idle_duration_seconds', 'waiting_time_seconds', 'waiting_time_formatted', 'zone_id', 'start_time_utc',
    'car_id', 'source_id', 
    'running_total', 'running_enter_count', 'running_exit_count', 'track_id',
  ];
};

const CONSOLIDATED_ALERTS_COLUMN_ORDER = [
  'event_timestamp_utc', 'mode', 'camera_display_name', 'alert_status', 'audio', 'alert_reason', 'details','concern'
];

// Helper function to check if an alert has negative customer sentiment
const isNegativeCustomerSentiment = (alert) => {
  // Check direct customer_sentiment property
  if (alert.customer_sentiment && 
      String(alert.customer_sentiment).toLowerCase() === 'negative') {
    return true;
  }
  
  // Check customer_sentiment in details object
  if (alert.details?.customer_sentiment && 
      String(alert.details.customer_sentiment).toLowerCase() === 'negative') {
    return true;
  }

  // Check alert_reason for sentiment: negative patterns
  if (alert.alert_reason) {
    const alertReasonLower = String(alert.alert_reason).toLowerCase();
    if (alertReasonLower.includes('sentiment:') && alertReasonLower.includes('negative')) {
      return true;
    }
    // Also check for direct negative value
    if (alertReasonLower === 'negative') {
      return true;
    }
  }

  // Check alert_reason in details object
  if (alert.details?.alert_reason) {
    const detailsAlertReasonLower = String(alert.details.alert_reason).toLowerCase();
    if (detailsAlertReasonLower.includes('sentiment:') && detailsAlertReasonLower.includes('negative')) {
      return true;
    }
    if (detailsAlertReasonLower === 'negative') {
      return true;
    }
  }
  
  return false;
};

// Helper function to extract playable audio URL from log entry
const getPlayableAudioUrl = (logEntry) => {
  // Debug logging to understand data structure
  console.log('[Audio Debug] Checking log entry for audio:', {
    hasAudioUrl: !!logEntry.audio_url,
    hasAudioS3Uri: !!logEntry.audio_s3_uri,
    hasDetailsAudioUrl: !!(logEntry.details?.audio_url),
    hasDetailsAudioS3Uri: !!(logEntry.details?.audio_s3_uri),
    logEntryKeys: Object.keys(logEntry),
    detailsKeys: logEntry.details ? Object.keys(logEntry.details) : 'No details object'
  });

  // Preferred: backend's presigned HTTPS link (top level)
  if (logEntry.audio_url) {
    console.log('[Audio Debug] Found audio_url:', logEntry.audio_url);
    return logEntry.audio_url;
  }

  // Check for audio URL in details object (for consolidated alerts)
  if (logEntry.details?.audio_url) {
    console.log('[Audio Debug] Found details.audio_url:', logEntry.details.audio_url);
    return logEntry.details.audio_url;
  }

  // Fallback: convert s3://bucket/key → https://bucket.s3.amazonaws.com/key (top level)
  if (logEntry.audio_s3_uri?.startsWith("s3://")) {
    const [, bucket, key] = logEntry.audio_s3_uri.match(/^s3:\/\/([^/]+)\/(.+)$/) || [];
    if (bucket && key) {
      const url = `https://${bucket}.s3.amazonaws.com/${key}`;
      console.log('[Audio Debug] Converted s3 URI to URL:', url);
      return url;
    }
  }

  // Fallback: convert s3://bucket/key → https://bucket.s3.amazonaws.com/key (in details)
  if (logEntry.details?.audio_s3_uri?.startsWith("s3://")) {
    const [, bucket, key] = logEntry.details.audio_s3_uri.match(/^s3:\/\/([^/]+)\/(.+)$/) || [];
    if (bucket && key) {
      const url = `https://${bucket}.s3.amazonaws.com/${key}`;
      console.log('[Audio Debug] Converted details.s3 URI to URL:', url);
      return url;
    }
  }

  console.log('[Audio Debug] No playable audio URL found');
  return null;
};

const HistoricalLogs = () => {
  const [modes, setModes] = useState([]);
  const [selectedMode, setSelectedMode] = useState(null);
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isLoadingModes, setIsLoadingModes] = useState(false);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [error, setError] = useState('');

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedSortOrder, setSelectedSortOrder] = useState(sortOptions[1]);

  const [availableLogTags, setAvailableLogTags] = useState([]);
  const [selectedLogTag, setSelectedLogTag] = useState(null);
  const [filterLongIdle, setFilterLongIdle] = useState(false);

  // Analysis mode filtering state (for Audio mode only)
  const [availableAnalysisModes, setAvailableAnalysisModes] = useState([]);
  const [selectedAnalysisMode, setSelectedAnalysisMode] = useState(null);

  const [viewMode, setViewMode] = useState('filtered');
  const [consolidatedAlerts, setConsolidatedAlerts] = useState([]);
  const [isLoadingConsolidatedAlerts, setIsLoadingConsolidatedAlerts] = useState(false);

  const [alertFilterSelectedModes, setAlertFilterSelectedModes] = useState([]);
  const [alertFilterSelectedCameras, setAlertFilterSelectedCameras] = useState([]);
  const [availableAlertCameras, setAvailableAlertCameras] = useState([]);
  const [selectedAlertStatus, setSelectedAlertStatus] = useState(null);

  // Pagination state variables
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  
  // Pagination state for consolidated alerts
  const [alertsCurrentPage, setAlertsCurrentPage] = useState(0);
  const [alertsTotalCount, setAlertsTotalCount] = useState(0);
  const [alertsHasMore, setAlertsHasMore] = useState(false);
  const [alertsTotalPages, setAlertsTotalPages] = useState(0);

  // Additional loading states for pagination operations
  const [isPaginating, setIsPaginating] = useState(false);

  // Debug: Track state changes
  useEffect(() => {
    console.log(`[HistoricalLogs] currentPage changed to: ${currentPage}`);
  }, [currentPage]);
  
  useEffect(() => {
    console.log(`[HistoricalLogs] alertsCurrentPage changed to: ${alertsCurrentPage}`);
  }, [alertsCurrentPage]);

  useEffect(() => {
    const fetchModes = async () => {
      setIsLoadingModes(true); setError('');
      try {
        const data = await getLogModes();
        
        // Defensive programming: ensure data.modes exists and is an array
        if (!data || !Array.isArray(data.modes)) {
          console.warn('[HistoricalLogs] getLogModes returned invalid data structure:', data);
          setModes([]);
          setError('Invalid response format for log modes.');
          return;
        }
        
        setModes(data.modes.map(modeSlug => ({
          value: modeSlug.toLowerCase(), // Ensure value is lowercase for consistent filtering
          label: modeSlug.charAt(0).toUpperCase() + modeSlug.slice(1)
        })));
      } catch (err) {
        console.error('[HistoricalLogs] Error loading log modes:', err);
        const errorMessage = err?.message || String(err) || 'Unknown error';
        toast.error(`Failed to load log modes: ${errorMessage}`);
        setError('Could not load log modes.');
      } finally {
        setIsLoadingModes(false);
      }
    };
    fetchModes();
  }, []);

  useEffect(() => {
    if (!selectedMode) {
      setSources([]); setSelectedSource(null); setLogs([]);
      setSelectedLogTag(null); setAvailableLogTags([]); setFilterLongIdle(false);
      // Reset analysis mode filters
      setAvailableAnalysisModes([]); setSelectedAnalysisMode(null);
      return;
    }
    if (selectedMode.label !== 'Idle') { setFilterLongIdle(false); }
    const fetchSources = async () => {
      setIsLoadingSources(true);
      setSelectedSource(null); setLogs([]); setSelectedLogTag(null);
      setAvailableLogTags([]); setError('');
      // Reset analysis mode filters when mode changes
      setAvailableAnalysisModes([]); setSelectedAnalysisMode(null);
      try {
        const data = await getLogSourcesForMode(selectedMode.value);
        
        // Defensive programming: ensure data.sources exists and is an array
        if (!data || !Array.isArray(data.sources)) {
          console.warn('[HistoricalLogs] getLogSourcesForMode returned invalid data structure:', data);
          setSources([]);
          setError('Invalid response format for log sources.');
          return;
        }
        
        setSources(data.sources.map(source => ({ value: source, label: source })));
      } catch (err) {
        console.error(`[HistoricalLogs] Error loading sources for ${selectedMode.label}:`, err);
        const errorMessage = err?.message || String(err) || 'Unknown error';
        toast.error(`Failed to load sources for ${selectedMode.label}: ${errorMessage}`);
        setError(`Could not load sources for ${selectedMode.label}.`);
      } finally {
        setIsLoadingSources(false);
      }
    };
    fetchSources();
  }, [selectedMode]);

  useEffect(() => {
    if (selectedMode?.label === 'Idle' && !selectedSource) {
        setFilterLongIdle(false);
    }
  }, [selectedSource, selectedMode]);

  const fetchFilteredLogsData = useCallback(async (pageOverride = null, resetPagination = false) => {
    if (!selectedMode || !selectedSource) {
      setLogs([]); 
      setTotalCount(0);
      setHasMore(false);
      setTotalPages(0);
      return;
    }
    
    // Prevent concurrent requests
    if (isLoadingLogs && !resetPagination) {
      console.log('[HistoricalLogs] Skipping fetchFilteredLogsData - already loading');
      return;
    }
    
    setIsLoadingLogs(true); setError('');
    let startTimeIso = startDate ? formatISO(startOfDay(startDate)) : null;
    let endTimeIso = endDate ? formatISO(endOfDay(endDate)) : null;

    if (startDate && endDate && startOfDay(startDate) > endOfDay(endDate)) {
        toast.error("Start date cannot be after end date.");
        setIsLoadingLogs(false); return;
    }

    // Use pageOverride directly when provided, otherwise use current page
    let targetPage = currentPage;
    if (pageOverride !== null) {
      targetPage = pageOverride;
    } else if (resetPagination) {
      targetPage = 0;
    }
    
    const skip = targetPage * pageSize;

    console.log(`[HistoricalLogs] fetchFilteredLogsData: targetPage=${targetPage}, pageSize=${pageSize}, skip=${skip}, resetPagination=${resetPagination}`);

    try {
      const sortOrderValue = selectedSortOrder?.value; // Respect user's actual selection
      console.log(`[HistoricalLogs] Calling API with user-selected sortOrder: ${sortOrderValue}`);
      
      const response = await getHistoricalLogsByDate(selectedMode.label, selectedSource.value, startTimeIso, endTimeIso, pageSize, skip, sortOrderValue);
      
      console.log(`[HistoricalLogs] API Response:`, {
        itemsCount: response.items?.length,
        totalCount: response.total_count,
        page: response.page,
        totalPages: response.total_pages,
        hasMore: response.has_more,
        sortOrder: sortOrderValue
      });
      
      // Update pagination metadata
      setTotalCount(response.total_count);
      setHasMore(response.has_more);
      setTotalPages(response.total_pages);
      // Update current page to the target page we just fetched
      setCurrentPage(targetPage);
      
      // Backend should return data in correct chronological order for pagination to work properly
      // No frontend sorting needed - just use the data as returned by backend
      console.log(`[HistoricalLogs] Setting ${response.items.length} log items for page ${targetPage}`);
      setLogs(response.items);
      setError('');
    } catch (err) {
      console.error('[HistoricalLogs] Error loading filtered logs:', err);
      const errorMessage = err?.message || String(err) || 'Unknown error';
      toast.error(`Failed to load filtered logs: ${errorMessage}`);
      setError('Could not load filtered logs.');
      setTotalCount(0);
      setHasMore(false);
      setTotalPages(0);
    } finally {
      setIsLoadingLogs(false);
    }
  }, [selectedMode, selectedSource, selectedSortOrder, startDate, endDate, pageSize]); // Removed currentPage from dependencies

  const fetchAllAlertsData = useCallback(async (pageOverride = null, resetPagination = false) => {
    // Prevent concurrent requests
    if (isLoadingConsolidatedAlerts && !resetPagination) {
      console.log('[HistoricalLogs] Skipping fetchAllAlertsData - already loading');
      return;
    }
    
    setIsLoadingConsolidatedAlerts(true);
    setError('');
    let alertsStartTimeIso = startDate ? formatISO(startOfDay(startDate)) : null;
    let alertsEndTimeIso = endDate ? formatISO(endOfDay(endDate)) : null;

    if (startDate && endDate && startOfDay(startDate) > endOfDay(endDate) && viewMode === 'consolidated_alerts') {
        toast.error("Start date cannot be after end date for alerts.");
        setIsLoadingConsolidatedAlerts(false); return;
    }

    // Use pageOverride directly when provided, otherwise use current alerts page
    let targetPage = alertsCurrentPage;
    if (pageOverride !== null) {
      targetPage = pageOverride;
    } else if (resetPagination) {
      targetPage = 0;
    }
    
    const skip = targetPage * pageSize;

    try {
      const sortOrderValue = selectedSortOrder?.value; // Respect user's actual selection
      console.log(`[HistoricalLogs] Calling consolidated alerts API with user-selected sortOrder: ${sortOrderValue}`);
      
      const response = await getConsolidatedAlerts(pageSize, alertsStartTimeIso, alertsEndTimeIso, selectedAlertStatus?.value || null, skip, sortOrderValue);
      
      // Update alerts pagination metadata
      setAlertsTotalCount(response.total_count);
      setAlertsHasMore(response.has_more);
      setAlertsTotalPages(response.total_pages);
      // Update current alerts page to the target page we just fetched
      setAlertsCurrentPage(targetPage);
      
      // Backend should return data in correct chronological order for pagination to work properly
      // No frontend sorting needed - just use the data as returned by backend
      console.log(`[HistoricalLogs] Setting ${response.items.length} alert items for page ${targetPage}`);
      setConsolidatedAlerts(response.items);
      setError('');
      if (response.items.length === 0) {
        let periodMessage = "the selected criteria";
        if (alertsStartTimeIso && alertsEndTimeIso) {
            periodMessage = `the period ${format(parseISO(alertsStartTimeIso), 'MMM dd, yyyy')} to ${format(parseISO(alertsEndTimeIso), 'MMM dd, yyyy')}`;
        } else if (alertsStartTimeIso) {
            periodMessage = `from ${format(parseISO(alertsStartTimeIso), 'MMM dd, yyyy')}`;
        } else if (alertsEndTimeIso) {
            periodMessage = `up to ${format(parseISO(alertsEndTimeIso), 'MMM dd, yyyy')}`;
        } else {
             periodMessage = "all time (up to limit)";
        }
        toast.info(`No consolidated alerts found for ${periodMessage}.`);
      }
    } catch (err) {
      console.error('[HistoricalLogs] Error loading consolidated alerts:', err);
      const errorMessage = err?.message || String(err) || 'Unknown error';
      toast.error(`Failed to load consolidated alerts: ${errorMessage}`);
      setError('Could not load consolidated alerts.');
      setAlertsTotalCount(0);
      setAlertsHasMore(false);
      setAlertsTotalPages(0);
    } finally {
      setIsLoadingConsolidatedAlerts(false);
    }
  }, [startDate, endDate, selectedSortOrder, viewMode, selectedAlertStatus, pageSize]); // Removed alertsCurrentPage from dependencies

  useEffect(() => {
    console.log(`[HistoricalLogs] Main useEffect triggered:`, {
      viewMode,
      selectedMode: selectedMode?.label,
      selectedSource: selectedSource?.value,
      startDate: startDate?.toISOString?.(),
      endDate: endDate?.toISOString?.(),
      sortOrder: selectedSortOrder?.value,
      alertStatus: selectedAlertStatus?.value,
      currentPage,
      alertsCurrentPage
    });
    
    if (viewMode === 'filtered') {
      if (selectedMode && selectedSource) {
        console.log('[HistoricalLogs] Main useEffect: Calling fetchFilteredLogsData with reset=true');
        fetchFilteredLogsData(null, true); // Reset pagination when dependencies change
      } else {
        console.log('[HistoricalLogs] Main useEffect: Clearing filtered logs state');
        setLogs([]);
        setTotalCount(0);
        setHasMore(false);
        setTotalPages(0);
        setCurrentPage(0);
      }
    } else if (viewMode === 'consolidated_alerts') {
      console.log('[HistoricalLogs] Main useEffect: Calling fetchAllAlertsData with reset=true');
      fetchAllAlertsData(null, true); // Reset pagination when dependencies change
    }
  }, [viewMode, selectedMode, selectedSource, startDate, endDate, selectedSortOrder, selectedAlertStatus]); // Removed functions from dependency array

  // Separate useEffect for page size changes only
  useEffect(() => {
    if (isPaginating) return; // Prevent calls during pagination
    
    console.log('[HistoricalLogs] Page size changed to:', pageSize);
    if (viewMode === 'filtered' && selectedMode && selectedSource) {
      console.log('[HistoricalLogs] Resetting filtered logs pagination due to page size change');
      fetchFilteredLogsData(0, true);
    } else if (viewMode === 'consolidated_alerts') {
      console.log('[HistoricalLogs] Resetting alerts pagination due to page size change');
      fetchAllAlertsData(0, true);
    }
  }, [pageSize]); // Only pageSize in dependency array
  
  const availableAlertFilterModes = useMemo(() => modes, [modes]);

  useEffect(() => {
    if (viewMode === 'consolidated_alerts' && Array.isArray(consolidatedAlerts) && consolidatedAlerts.length > 0) {
      // Defensive programming: ensure consolidatedAlerts is an array before mapping
      const uniqueCameraObjects = [...new Set(consolidatedAlerts.map(alert => alert.camera_display_name).filter(Boolean))]
        .sort()
        .map(camera => ({ value: camera, label: camera }));
      setAvailableAlertCameras(uniqueCameraObjects);

      setAlertFilterSelectedCameras(prevSelected =>
        prevSelected.filter(camValue => uniqueCameraObjects.some(uc => uc.value === camValue))
      );
    } else if (viewMode === 'consolidated_alerts' && consolidatedAlerts.length === 0) {
      setAvailableAlertCameras([]);
    } else if (viewMode !== 'consolidated_alerts') {
      setAvailableAlertCameras([]);
    }
  }, [consolidatedAlerts, viewMode]);


  useEffect(() => {
    if (viewMode === 'filtered' && selectedMode && MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label] && Array.isArray(logs) && logs.length > 0) {
      const config = MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label];
      
      // For Audio mode, use dynamic field mapping; for others, use traditional tagColumn approach
      if (selectedMode.label === 'Audio') {
        // Use dynamic tags from multiple analysis modes
        const uniqueTags = getAvailableTagsFromLogs(logs, selectedMode);
        if (uniqueTags.length > 0) {
          setAvailableLogTags([{ value: 'ALL_TAGS', label: 'All Types' }, ...uniqueTags.map(tag => ({ value: tag, label: tag }))]);
        } else {
          setAvailableLogTags([]);
        }
      } else {
        // Traditional mode - use tagColumn approach
        const tagColumn = config.tagColumn;
        if (tagColumn && logs[0] && Object.prototype.hasOwnProperty.call(logs[0], tagColumn)) {
          // Defensive programming: ensure logs is an array before mapping
          const uniqueTags = [...new Set(logs.map(log => log[tagColumn]).filter(tag => tag != null))]
            .sort().map(tag => ({ value: String(tag), label: String(tag) }));
          setAvailableLogTags([{ value: 'ALL_TAGS', label: 'All Types' }, ...uniqueTags]);
        } else {
          setAvailableLogTags([]);
        }
      }
    } else {
      setAvailableLogTags([]);
      if (viewMode === 'filtered') {
        // For Audio mode, check if we have logs with analysis fields; for others, check tagColumn
        const currentModeHasTagColumn = selectedMode && (
          selectedMode.label === 'Audio' ?
          (logs.length > 0 && (logs[0].customer_sentiment !== undefined || logs[0].classification !== undefined)) :
          MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label]?.tagColumn
        );
        if (!currentModeHasTagColumn) { setSelectedLogTag(null); }
      }
    }
  }, [logs, selectedMode, viewMode]);

  // useEffect to populate available analysis modes when logs change (for Audio mode only)
  useEffect(() => {
    if (viewMode === 'filtered' && selectedMode && selectedMode.label === 'Audio' && Array.isArray(logs) && logs.length > 0) {
      const modes = getAvailableAnalysisModesFromLogs(logs, selectedMode);
      setAvailableAnalysisModes(modes);

      // If we have a selected analysis mode but it's no longer available, reset it
      if (selectedAnalysisMode && selectedAnalysisMode.value !== 'ALL_MODES' && !modes.includes(selectedAnalysisMode.value)) {
        setSelectedAnalysisMode(null);
      }
    } else {
      setAvailableAnalysisModes([]);
      setSelectedAnalysisMode(null);
    }
  }, [logs, selectedMode, viewMode, selectedAnalysisMode]);

  const displayedLogItems = useMemo(() => {
    if (viewMode === 'consolidated_alerts') {
      let filteredAlerts = [...consolidatedAlerts];
      if (alertFilterSelectedModes.length > 0) {
        filteredAlerts = filteredAlerts.filter(alert =>
          alert.mode && alertFilterSelectedModes.includes(String(alert.mode).toLowerCase())
        );
      }
      if (alertFilterSelectedCameras.length > 0) {
        filteredAlerts = filteredAlerts.filter(alert =>
          alert.camera_display_name && alertFilterSelectedCameras.includes(alert.camera_display_name)
        );
      }
      // Filter out negative customer sentiment alerts
      filteredAlerts = filteredAlerts.filter(alert => !isNegativeCustomerSentiment(alert));
      return filteredAlerts;
    }

    let filtered = [...logs];
    if (selectedMode && selectedLogTag && selectedLogTag.value !== 'ALL_TAGS' && MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label]) {
      const config = MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label];
      
      // For Audio mode, use dynamic field mapping; for others, use traditional tagColumn approach
      if (selectedMode.label === 'Audio') {
        // Filter based on dynamic primary field (customer_sentiment, classification, etc.)
        filtered = filtered.filter(log => {
          const primaryValue = getPrimaryFieldValue(log);
          return primaryValue && String(primaryValue) === selectedLogTag.value;
        });
      } else {
        // Traditional mode - use tagColumn approach
        const tagColumn = config.tagColumn;
        if (tagColumn && filtered.length > 0 && Object.prototype.hasOwnProperty.call(filtered[0], tagColumn)) {
          filtered = filtered.filter(log => String(log[tagColumn]) === selectedLogTag.value);
        }
      }
    }
    // Filter by analysis mode for Audio mode only
    if (selectedMode?.label === 'Audio' && selectedAnalysisMode && selectedAnalysisMode.value !== 'ALL_MODES') {
      filtered = filtered.filter(log => {
        const logAnalysisMode = detectAnalysisMode(log);
        return logAnalysisMode === selectedAnalysisMode.value;
      });
    }
    if (selectedMode?.label === 'Idle' && filterLongIdle) {
      const idleConfig = MODE_SPECIFIC_CONFIG_CLIENT['Idle'];
      if (idleConfig && idleConfig.idleDurationColumn && typeof idleConfig.idleDurationThreshold === 'number') {
        filtered = filtered.filter(log => {
          const duration = log[idleConfig.idleDurationColumn];
          return duration != null && Number(duration) >= idleConfig.idleDurationThreshold;
        });
      }
    }
    return filtered;
  }, [
      viewMode, logs, consolidatedAlerts, selectedLogTag, selectedMode, filterLongIdle,
      alertFilterSelectedModes, alertFilterSelectedCameras, selectedAnalysisMode
    ]);

  const getColumnClass = (key, logItem = null) => {
    let classes = '';
    if (key === 'event_timestamp_utc') classes += ' col-timestamp';
    else if (key === 'transcript' || (viewMode === 'consolidated_alerts' && key === 'details')) {
      classes += ' col-transcript';
      // Add special class if this cell has audio
      if (logItem && getPlayableAudioUrl(logItem)) {
        classes += ' col-transcript-with-audio';
        console.log('[Audio Debug] Added audio class to column:', key, 'in view:', viewMode);
      }
    }
    else if (key === 'concern') classes += ' col-concern';
    else if (key === 'solution_provided') classes += ' col-solution-provided';
    else if (key === 'customer_sentiment') classes += ' col-customer-sentiment';
    // New analysis mode field mappings
    else if (key === 'classification') classes += ' col-customer-sentiment'; // Maps to same styling as customer_sentiment
    else if (key === 'evidence') classes += ' col-concern'; // Maps to same styling as concern
    else if (key === 'confidence') classes += ' col-solution-provided'; // Maps to same styling as solution_provided
    else if (key === 'alert_reason') classes += ' col-alert-reason';
    else if (key === 'source_type') classes += ' col-source-type';
    else if (key === 'mode') classes += ' col-mode';
    else if (key === 'level') classes += ' col-log-level';
    else if (key === 'message') classes += ' col-message';
    else if (key === 'idle_duration_seconds') classes += ' col-idle-duration';
    else if (key === 'audio') classes += ' col-audio';
    else if (key === 'alert_status') classes += ' col-alert-status';
    else if (key === 'source_id') classes += ' col-source-id';
    else if (key === 'camera_display_name') classes += ' col-camera-display-name';
    else if (key === 'details') classes += ' col-details';

    if (viewMode === 'filtered' && logItem && selectedMode && MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label]) {
      const config = MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label];
      if (config.tagColumn && key === config.tagColumn && config.alertTags && config.alertTags.length > 0) {
        if (config.alertTags.includes(String(logItem[config.tagColumn]))) {
          classes += ' col-alert-tag';
        }
      }
      if (selectedMode.label === 'Idle' && config.idleDurationColumn && key === config.idleDurationColumn &&
          logItem[config.idleDurationColumn] != null && typeof config.idleDurationThreshold === 'number' &&
          Number(logItem[config.idleDurationColumn]) >= config.idleDurationThreshold) {
        classes += ' col-idle-alert';
      }
    } else if (viewMode === 'consolidated_alerts' && logItem && logItem.mode) {
      const currentAlertMode = String(logItem.mode).toLowerCase();
        if (key === 'alert_reason') {
          if (currentAlertMode === 'audio' || currentAlertMode === 'bounce') {
            const alertReasonLower = String(logItem.alert_reason).toLowerCase();
            if (
                alertReasonLower.includes('alert') ||
                alertReasonLower.includes('high_bounce_risk') ||
                alertReasonLower.includes('bounce_occurred_alert') ||
                alertReasonLower.includes('sentiment:') // Include all sentiment-related alerts
            ) {
                classes += ' col-alert-tag';
            }
          } else if (currentAlertMode === 'idle') {
             if (String(logItem.alert_reason).toLowerCase().includes('long idle')) {
                classes += ' col-idle-alert';
             }
          }
        }
    }
    return classes.trim();
  };

  const renderLogValue = (value, key, logItem = null) => {
    const valStr = value != null ? String(value) : 'N/A';
    const indianTimeZone = 'Asia/Kolkata';

    if (key === 'event_timestamp_utc' && typeof value === 'string') {
      try { return formatInTimeZone(value, indianTimeZone, "yyyy-MM-dd HH:mm:ss"); }
      catch (e) { console.warn("Failed to format timestamp:", value, e); return valStr; }
    }
    if (key === 'idle_duration_seconds' && typeof value === 'number' && viewMode === 'filtered') {
        const minutes = Math.floor(value / 60);
        const seconds = (value % 60).toFixed(0);
        return `${minutes}m ${seconds}s`;
    }
     if (typeof value === 'boolean') {
      return value.toString();
    }
    if (key === 'details' && viewMode === 'consolidated_alerts') {
        if (typeof value === 'string') {
            return <div className="scrollable-cell-content" title={value}>{value}</div>;
        } else if (typeof value === 'object' && value !== null) {
            if (value.hasOwnProperty('transcript') && Object.keys(value).length === 1 && typeof value.transcript === 'string') {
                 return <div className="scrollable-cell-content" title={value.transcript}>{value.transcript}</div>;
            }
            return <pre className="log-value-pre">{JSON.stringify(value, null, 2)}</pre>;
        }
        return 'N/A';
    }
    if (typeof value === 'object' && value !== null) {
      return <pre className="log-value-pre">{JSON.stringify(value, null, 2)}</pre>;
    }
    // PRIORITY 1: Handle virtual 'audio' column for consolidated alerts FIRST
    if (key === 'audio' && viewMode === 'consolidated_alerts') {
      const audioUrl = logItem ? getPlayableAudioUrl(logItem) : null;
      if (audioUrl) {
        return (
          <div className="log-cell-with-audio">
            <div className="log-audio-player">
              <CustomAudioPlayer 
                src={audioUrl}
                preload="none"
                className="inline-historical-audio-player"
              />
            </div>
          </div>
        );
      } else {
        return '—'; // Show dash when no audio available
      }
    }

    const scrollableFilteredKeys = ['transcript', 'concern', 'solution_provided', 'evidence', 'confidence', 'message', 'source_id'];
    const scrollableConsolidatedKeys = ['alert_reason', 'camera_display_name','concern', 'details'];
    
    // PRIORITY 2: Audio detection for filtered logs ONLY (transcript column)
    const audioUrl = logItem ? getPlayableAudioUrl(logItem) : null;
    let shouldShowAudioPlayer = false;
    
    if (audioUrl && viewMode === 'filtered') {
      // In filtered view, show audio player in transcript column
      shouldShowAudioPlayer = key === 'transcript';
      
      console.log('[Audio Debug] Filtered view audio:', {
        key: key,
        shouldShowAudioPlayer: shouldShowAudioPlayer,
        hasAudioUrl: !!audioUrl
      });
    }
    
    // SCROLLABLE CONTENT LOGIC SECOND
    let currentScrollableKeys = [];
    if (viewMode === 'filtered') currentScrollableKeys = scrollableFilteredKeys;
    else if (viewMode === 'consolidated_alerts') currentScrollableKeys = scrollableConsolidatedKeys;
    
    const isScrollableKey = currentScrollableKeys.includes(key) && typeof value === 'string';
    
    // HYBRID RENDERING: Handle audio + scrollable combination
    if (shouldShowAudioPlayer && isScrollableKey) {
      // Both audio player AND scrollable content needed
      const className = (key === 'source_id' || key === 'camera_display_name') ? "scrollable-cell-content source-id-content" : "scrollable-cell-content";
      return (
        <div className="log-cell-with-audio">
          <div className={`log-text-content ${className}`} title={valStr}>
            {valStr}
          </div>
          <div className="log-audio-player">
            <CustomAudioPlayer 
              src={audioUrl}
              preload="none"
              className="inline-historical-audio-player"
            />
          </div>
        </div>
      );
    } else if (shouldShowAudioPlayer) {
      // Audio player only (no scrollable needed)
      return (
        <div className="log-cell-with-audio">
          <div className="log-text-content" title={valStr}>
            {valStr}
          </div>
          <div className="log-audio-player">
            <CustomAudioPlayer 
              src={audioUrl}
              preload="none"
              className="inline-historical-audio-player"
            />
          </div>
        </div>
      );
    } else if (isScrollableKey) {
      // Scrollable content only (no audio)
      const className = (key === 'source_id' || key === 'camera_display_name') ? "scrollable-cell-content source-id-content" : "scrollable-cell-content";
      return <div className={className} title={valStr}>{valStr}</div>;
    }
    
    return valStr;
  };

  const currentColumnOrder = useMemo(() => {
    if (viewMode === 'consolidated_alerts') {
      return CONSOLIDATED_ALERTS_COLUMN_ORDER;
    }
    // Use dynamic column order based on logs and selected mode
    return getDynamicColumnOrder(displayedLogItems, selectedMode);
  }, [viewMode, displayedLogItems, selectedMode]);

  const logKeysToDisplay = useMemo(() => {
    if (displayedLogItems.length === 0) return [];
    if (viewMode === 'consolidated_alerts') {
        return CONSOLIDATED_ALERTS_COLUMN_ORDER.filter(key => {
            // Special handling for virtual 'audio' column
            if (key === 'audio') {
                return displayedLogItems.some(log => getPlayableAudioUrl(log) !== null);
            }
            // Regular column filtering
            return displayedLogItems.some(log => Object.prototype.hasOwnProperty.call(log, key) && log[key] !== null && log[key] !== undefined);
        });
    }
    const allKeysInLogs = new Set();
    displayedLogItems.forEach(log => { Object.keys(log).forEach(key => allKeysInLogs.add(key)); });
    
    // Use dynamic column order instead of hardcoded COLUMN_ORDER_FILTERED
    const orderedKeys = currentColumnOrder.filter(preferredKey => allKeysInLogs.has(preferredKey));
    if (allKeysInLogs.has('event_timestamp_utc') && orderedKeys[0] !== 'event_timestamp_utc') {
        const index = orderedKeys.indexOf('event_timestamp_utc');
        if (index > -1) orderedKeys.splice(index, 1);
        orderedKeys.unshift('event_timestamp_utc');
    }
    return orderedKeys;
  }, [displayedLogItems, viewMode, currentColumnOrder]);

  const noFilteredLogsMessageText = useMemo(() => {
    if (viewMode !== 'filtered' || !(selectedMode && selectedSource && logs.length > 0 && displayedLogItems.length === 0 && !error)) {
        return "";
    }
    const activeFilters = [];
    const modeConfig = selectedMode ? MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label] : null;
    if (selectedLogTag && selectedLogTag.value !== 'ALL_TAGS') {
        const tagLabel = modeConfig?.tagColumn?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Type/Event';
        activeFilters.push(`'${selectedLogTag.label}' ${tagLabel}`);
    }
    if (selectedMode?.label === 'Idle' && filterLongIdle) {
        activeFilters.push("idle duration (>= 5 min)");
    }
    if (activeFilters.length > 0) {
        return `No logs found matching the ${activeFilters.join(' and ')} filter(s) for the selected date range.`;
    }
    return "No logs displayed for the selected criteria after filtering.";
  }, [viewMode, logs, displayedLogItems, selectedMode, selectedSource, filterLongIdle, selectedLogTag, error]);

  const noConsolidatedAlertsMessageText = useMemo(() => {
    if (viewMode !== 'consolidated_alerts' || isLoadingConsolidatedAlerts || error || displayedLogItems.length > 0) {
        return "";
    }
    if (consolidatedAlerts.length === 0 && !isLoadingConsolidatedAlerts && !error) {
        return "No consolidated alerts were found for the selected date range.";
    }
    if (consolidatedAlerts.length > 0 && displayedLogItems.length === 0) {
        const activeFilterDescriptions = [];
        if (alertFilterSelectedModes.length > 0) {
            activeFilterDescriptions.push("selected Mode(s)");
        }
        if (alertFilterSelectedCameras.length > 0) {
            activeFilterDescriptions.push("selected Camera(s)");
        }
        if (activeFilterDescriptions.length > 0) {
            return `No alerts match the currently ${activeFilterDescriptions.join(' and ')}. Try adjusting these filters or the date range.`;
        }
    }
    return "No consolidated alerts to display with the current filter settings.";
  }, [
      viewMode, isLoadingConsolidatedAlerts, error, displayedLogItems, consolidatedAlerts,
      alertFilterSelectedModes, alertFilterSelectedCameras
  ]);

  const handleAlertModeChange = useCallback((modeValue) => {
    setAlertFilterSelectedModes(prev =>
      prev.includes(modeValue)
        ? prev.filter(m => m !== modeValue)
        : [...prev, modeValue]
    );
  }, []);

  const handleAlertCameraChange = useCallback((cameraValue) => {
    setAlertFilterSelectedCameras(prev =>
      prev.includes(cameraValue)
        ? prev.filter(c => c !== cameraValue)
        : [...prev, cameraValue]
    );
  }, []);

  return (
    <div className="historical-logs-page-container">
      <div className="filters-sidebar">
        <div className="view-mode-toggle-header">
          <h2 className="sidebar-title" style={{ marginBottom: '0' }}>
            {viewMode === 'filtered' ? 'Filtered Logs View' : 'All Alerts View'}
          </h2>
          <label
            className="toggle-switch"
            title={viewMode === 'filtered' ? "Switch to All Alerts View" : "Switch to Filtered Logs View"}
          >
            <input
              type="checkbox"
              checked={viewMode === 'consolidated_alerts'}
              onChange={() => {
                setViewMode(prev => prev === 'filtered' ? 'consolidated_alerts' : 'filtered');
              }}
            />
            <span className="slider round"></span>
          </label>
        </div>

        {viewMode === 'filtered' && (
          <>
            <div className="filter-group">
              <label htmlFor="mode-select" className="filter-label">Log Mode:</label>
              <Select inputId="mode-select" className="react-select-container" classNamePrefix="react-select"
                options={modes} value={selectedMode} onChange={setSelectedMode}
                isLoading={isLoadingModes} placeholder="Select Mode..." isClearable />
            </div>
            {selectedMode && (
              <div className="filter-group">
                <label htmlFor="source-select" className="filter-label">Camera/Source:</label>
                <Select inputId="source-select" className="react-select-container" classNamePrefix="react-select"
                  options={sources} value={selectedSource}
                  onChange={(option) => {
                    setSelectedSource(option);
                    setSelectedLogTag(null);
                    // Reset analysis mode filters when source changes
                    setSelectedAnalysisMode(null);
                  }}
                  isLoading={isLoadingSources} placeholder="Select Source..."
                  isDisabled={!selectedMode || isLoadingSources} isClearable />
              </div>
            )}
            {selectedMode && selectedSource && MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label]?.tagColumn && availableLogTags.length > 0 && (
              <div className="filter-group">
                <label htmlFor="log-tag-select" className="filter-label">
                  {MODE_SPECIFIC_CONFIG_CLIENT[selectedMode.label]?.tagColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Log Type / Event'}:
                </label>
                <Select inputId="log-tag-select" className="react-select-container" classNamePrefix="react-select"
                  options={availableLogTags} value={selectedLogTag} onChange={setSelectedLogTag}
                  placeholder="Select Type..." isClearable isDisabled={isLoadingLogs} />
              </div>
            )}
            {selectedMode?.label === 'Audio' && selectedSource && availableAnalysisModes.length > 0 && (
              <div className="filter-group">
                <label htmlFor="analysis-mode-select" className="filter-label">Analysis Mode:</label>
                <Select inputId="analysis-mode-select" className="react-select-container" classNamePrefix="react-select"
                  options={[
                    { value: 'ALL_MODES', label: 'All Analysis Modes' },
                    ...availableAnalysisModes.map(mode => ({ value: mode, label: mode.replace(/_/g, ' ') }))
                  ]}
                  value={selectedAnalysisMode}
                  onChange={setSelectedAnalysisMode}
                  placeholder="Select Analysis Mode..."
                  isClearable
                  isDisabled={isLoadingLogs} />
              </div>
            )}
            {selectedMode?.label === 'Idle' && selectedSource && (
              <div className="filter-group">
                <label htmlFor="long-idle-filter" className="filter-label filter-label-checkbox">
                  <input type="checkbox" id="long-idle-filter" className="filter-checkbox"
                    checked={filterLongIdle} onChange={(e) => setFilterLongIdle(e.target.checked)}
                    disabled={isLoadingLogs} />
                  Only show idle times {'>'}= 5 minutes
                </label>
              </div>
            )}
          </>
        )}

        {viewMode === 'consolidated_alerts' && (
          <>
            {availableAlertFilterModes.length > 0 && !isLoadingModes ? (
              <div className="filter-group">
                <label className="filter-label">Filter by Mode:</label>
                <div className="checkbox-group">
                  {availableAlertFilterModes.map(mode => (
                    <label key={mode.value} className="filter-label-checkbox">
                      <input
                        type="checkbox"
                        className="filter-checkbox"
                        value={mode.value} 
                        checked={alertFilterSelectedModes.includes(mode.value)}
                        onChange={() => handleAlertModeChange(mode.value)}
                        disabled={isLoadingConsolidatedAlerts}
                      />
                      {mode.label}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              !isLoadingModes && <p className="filter-info-text">Log modes not available for filtering.</p>
            )}

            {availableAlertCameras.length > 0 ? (
              <div className="filter-group">
                <label className="filter-label">Filter by Camera:</label>
                <div className="checkbox-group">
                  {availableAlertCameras.map(camera => (
                    <label key={camera.value} className="filter-label-checkbox">
                      <input
                        type="checkbox"
                        className="filter-checkbox"
                        value={camera.value}
                        checked={alertFilterSelectedCameras.includes(camera.value)}
                        onChange={() => handleAlertCameraChange(camera.value)}
                        disabled={isLoadingConsolidatedAlerts}
                      />
                      {camera.label}
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              viewMode === 'consolidated_alerts' && !isLoadingConsolidatedAlerts && consolidatedAlerts.length > 0 &&
              <p className="filter-info-text">No camera names in current alerts for filtering.</p>
            )}
             {viewMode === 'consolidated_alerts' && !isLoadingConsolidatedAlerts && consolidatedAlerts.length === 0 && availableAlertCameras.length === 0 && (
                <p className="filter-info-text">Load alerts to see camera filters.</p>
            )}

            <div className="filter-group">
              <label htmlFor="alert-status-select" className="filter-label">Filter by Alert Status:</label>
              <Select 
                inputId="alert-status-select" 
                className="react-select-container" 
                classNamePrefix="react-select"
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'resolved', label: 'Resolved' },
                  { value: 'acknowledged', label: 'Acknowledged' },
                  { value: 'pending', label: 'Pending' }
                ]} 
                value={selectedAlertStatus} 
                onChange={setSelectedAlertStatus}
                placeholder="All Statuses" 
                isClearable
                isDisabled={isLoadingConsolidatedAlerts}
              />
            </div>
          </>
        )}

        { ((viewMode === 'filtered' && selectedMode && selectedSource) || viewMode === 'consolidated_alerts' ) && (
          <>
            <div className="filter-group">
              <label htmlFor="start-date-select" className="filter-label">Start Date:</label>
              <input type="date" id="start-date-select" className="date-picker-input"
                value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setStartDate(e.target.value ? parseISO(e.target.value) : null)}
                disabled={isLoadingLogs || isLoadingConsolidatedAlerts} />
            </div>
            <div className="filter-group">
              <label htmlFor="end-date-select" className="filter-label">End Date:</label>
              <input type="date" id="end-date-select" className="date-picker-input"
                value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setEndDate(e.target.value ? parseISO(e.target.value) : null)}
                min={startDate ? format(startDate, 'yyyy-MM-dd') : undefined}
                disabled={isLoadingLogs || isLoadingConsolidatedAlerts} />
            </div>
            <div className="filter-group">
                <label htmlFor="sort-order-select" className="filter-label">Sort By Timestamp:</label>
                <Select inputId="sort-order-select" className="react-select-container" classNamePrefix="react-select"
                    options={sortOptions} value={selectedSortOrder} onChange={setSelectedSortOrder}
                    isDisabled={isLoadingLogs || isLoadingConsolidatedAlerts} />
            </div>
          </>
        )}

        {viewMode === 'consolidated_alerts' && (
            <p style={{color: '#64748B', fontSize: '14px', textAlign: 'center', marginTop: '20px', padding: '0 10px'}}>
                Showing global alerts. Use date, sort, and specific filters above to refine.
            </p>
        )}
      </div>

      <div className="logs-main-content">
        <h1 className="main-content-title">
          {viewMode === 'filtered' 
            ? 'Historical Logs' 
            : `Consolidated System Alerts${!isLoadingConsolidatedAlerts ? ` (${displayedLogItems.length})` : ''}`
          }
        </h1>

        {error && <p className="error-message">{error}</p>}
        {(isLoadingLogs && viewMode === 'filtered') && <p className="loading-message">Loading filtered logs...</p>}
        {(isLoadingConsolidatedAlerts && viewMode === 'consolidated_alerts') && <p className="loading-message">Loading consolidated alerts...</p>}

        {((viewMode === 'filtered' && !isLoadingLogs) || (viewMode === 'consolidated_alerts' && !isLoadingConsolidatedAlerts)) && !error && displayedLogItems.length > 0 && (
          <div className="logs-table-wrapper">
             <table className="logs-table">
              <thead>
                <tr>
                  {logKeysToDisplay.map(key => (
                    <th key={key} className={getColumnClass(key)}>
                      {getColumnHeaderLabel(key, displayedLogItems, selectedMode)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedLogItems.map((log, index) => (
                  <tr key={log.details?.event_id || `${log.event_timestamp_utc || 'alert'}-${index}-${log.source_id || log.camera_display_name || uuidv4()}`}>
                    {logKeysToDisplay.map(key => (
                      <td key={`${key}-${index}`} className={getColumnClass(key, log)}>
                        {renderLogValue(log[key], key, log)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {((viewMode === 'filtered' && !isLoadingLogs && totalPages > 1) || (viewMode === 'consolidated_alerts' && !isLoadingConsolidatedAlerts && alertsTotalPages > 1)) && !error && (
          <div className="pagination-controls" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            margin: '20px 0', 
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label htmlFor="page-size-select" style={{ fontSize: '14px', fontWeight: '500' }}>Items per page:</label>
              <select 
                id="page-size-select"
                value={pageSize} 
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={{ 
                  padding: '5px 10px', 
                  borderRadius: '4px', 
                  border: '1px solid #ccc',
                  fontSize: '14px'
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
            
            <div style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              textAlign: 'center'
            }}>
              {viewMode === 'filtered' 
                ? `Showing ${Math.min((currentPage * pageSize) + 1, totalCount)} - ${Math.min((currentPage + 1) * pageSize, totalCount)} of ${totalCount} items`
                : `Showing ${Math.min((alertsCurrentPage * pageSize) + 1, alertsTotalCount)} - ${Math.min((alertsCurrentPage + 1) * pageSize, alertsTotalCount)} of ${alertsTotalCount} items`
              }
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button 
                onClick={async () => {
                  setIsPaginating(true);
                  try {
                    if (viewMode === 'filtered') {
                      await fetchFilteredLogsData(0);
                    } else {
                      await fetchAllAlertsData(0);
                    }
                  } finally {
                    setIsPaginating(false);
                  }
                }}
                disabled={viewMode === 'filtered' ? currentPage === 0 || isPaginating : alertsCurrentPage === 0 || isPaginating}
                style={{ 
                  padding: '6px 12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  backgroundColor: '#fff',
                  cursor: viewMode === 'filtered' ? (currentPage === 0 ? 'not-allowed' : 'pointer') : (alertsCurrentPage === 0 ? 'not-allowed' : 'pointer'),
                  opacity: viewMode === 'filtered' ? (currentPage === 0 ? 0.5 : 1) : (alertsCurrentPage === 0 ? 0.5 : 1),
                  fontSize: '14px'
                }}
              >
                {isPaginating ? '...' : 'First'}
              </button>
              
              <button 
                onClick={async () => {
                  setIsPaginating(true);
                  try {
                    if (viewMode === 'filtered') {
                      await fetchFilteredLogsData(currentPage - 1);
                    } else {
                      await fetchAllAlertsData(alertsCurrentPage - 1);
                    }
                  } finally {
                    setIsPaginating(false);
                  }
                }}
                disabled={viewMode === 'filtered' ? currentPage === 0 || isPaginating : alertsCurrentPage === 0 || isPaginating}
                style={{ 
                  padding: '6px 12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  backgroundColor: '#fff',
                  cursor: viewMode === 'filtered' ? (currentPage === 0 ? 'not-allowed' : 'pointer') : (alertsCurrentPage === 0 ? 'not-allowed' : 'pointer'),
                  opacity: viewMode === 'filtered' ? (currentPage === 0 ? 0.5 : 1) : (alertsCurrentPage === 0 ? 0.5 : 1),
                  fontSize: '14px'
                }}
              >
                {isPaginating ? '...' : 'Previous'}
              </button>
              
              <span style={{ 
                padding: '6px 12px', 
                backgroundColor: '#e9ecef', 
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Page {viewMode === 'filtered' ? currentPage + 1 : alertsCurrentPage + 1} of {viewMode === 'filtered' ? totalPages : alertsTotalPages}
              </span>
              
              <button 
                onClick={async () => {
                  setIsPaginating(true);
                  try {
                    if (viewMode === 'filtered') {
                      await fetchFilteredLogsData(currentPage + 1);
                    } else {
                      await fetchAllAlertsData(alertsCurrentPage + 1);
                    }
                  } finally {
                    setIsPaginating(false);
                  }
                }}
                disabled={viewMode === 'filtered' ? !hasMore || isPaginating : !alertsHasMore || isPaginating}
                style={{ 
                  padding: '6px 12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  backgroundColor: '#fff',
                  cursor: viewMode === 'filtered' ? (!hasMore ? 'not-allowed' : 'pointer') : (!alertsHasMore ? 'not-allowed' : 'pointer'),
                  opacity: viewMode === 'filtered' ? (!hasMore ? 0.5 : 1) : (!alertsHasMore ? 0.5 : 1),
                  fontSize: '14px'
                }}
              >
                {isPaginating ? '...' : 'Next'}
              </button>
              
              <button 
                onClick={async () => {
                  setIsPaginating(true);
                  try {
                    if (viewMode === 'filtered') {
                      await fetchFilteredLogsData(totalPages - 1);
                    } else {
                      await fetchAllAlertsData(alertsTotalPages - 1);
                    }
                  } finally {
                    setIsPaginating(false);
                  }
                }}
                disabled={viewMode === 'filtered' ? !hasMore || isPaginating : !alertsHasMore || isPaginating}
                style={{ 
                  padding: '6px 12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px', 
                  backgroundColor: '#fff',
                  cursor: viewMode === 'filtered' ? (!hasMore ? 'not-allowed' : 'pointer') : (!alertsHasMore ? 'not-allowed' : 'pointer'),
                  opacity: viewMode === 'filtered' ? (!hasMore ? 0.5 : 1) : (!alertsHasMore ? 0.5 : 1),
                  fontSize: '14px'
                }}
              >
                {isPaginating ? '...' : 'Last'}
              </button>
            </div>
          </div>
        )}
        
        {viewMode === 'filtered' && !isLoadingLogs && !error && (
            <>
                {!selectedMode && !isLoadingModes && <p className="no-logs-message">Please select a Log Mode to begin.</p>}
                {selectedMode && !selectedSource && !isLoadingSources && sources.length > 0 && <p className="no-logs-message">Please select a Camera/Source to view logs.</p>}
                {selectedMode && !isLoadingSources && sources.length === 0 && !error && <p className="no-logs-message">No sources available for the selected Log Mode.</p>}
                {selectedMode && selectedSource && logs.length === 0 && !error && <p className="no-logs-message">{`No logs found for ${selectedMode.label} - ${selectedSource.label} for the selected date range.`}</p>}
                {selectedMode && selectedSource && logs.length > 0 && displayedLogItems.length === 0 && !error && <p className="no-logs-message">{noFilteredLogsMessageText}</p>}
            </>
        )}
        {viewMode === 'consolidated_alerts' && !isLoadingConsolidatedAlerts && !error && displayedLogItems.length === 0 && noConsolidatedAlertsMessageText && (
            <p className="no-logs-message">{noConsolidatedAlertsMessageText}</p>
        )}
      </div>
    </div>
  );
};

const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

export default HistoricalLogs;